
import { defineComponent, inject, PropType } from "vue";
import WorkItem from "modules/construction-work/components/WorkItem.vue";
import AlertSearch from "@app/vue/AlertSearch/index.vue";
import Paginate from "@app/vue/Paginator/index.vue";
import WorkListSkeleton from "./WorkListSkeleton.vue";
import { ConstructionWork } from "modules/construction-work/src/Common/interface";
import { Store } from "modules/construction-work/src/Store";
import { ConstructionWorkApi } from "modules/construction-work/src/Api/ConstructionWorkApi";

export default defineComponent({
    name: "WorkList",
    components: { WorkItem, AlertSearch, Paginate, WorkListSkeleton },
    props: {
        initialWorks: {
            type: Array as PropType<ConstructionWork[]>,
            default: () => [],
        },
        initialTotal: {
            type: Number,
            default: 0,
        },
        isArchiveMode: {
            type: Boolean,
            default: false,
        },
        count: {
            type: Number,
            default: 20,
        },
    },
    setup() {
        const store = inject<Store>("store");
        return {
            store,
        };
    },
    data() {
        return {
            works: [] as ConstructionWork[],
            total: 0,
            loading: false,
            currentPage: 1,
            paginationKey: 1,
            lastAppliedFilters: [] as any[],
            initialFilters: {} as Record<string, any>,
            showVueListCW: false,
        };
    },
    computed: {
        statuses() {
            return this.store?.initData?.statuses || {};
        },
        executors() {
            return this.store?.initData?.executors || {};
        },
        categories() {
            return this.store?.initData?.categories || {};
        },
        showPagination(): boolean {
            if (!this.total || this.total === 0) return false;
            const pages = Math.ceil(this.total / this.count);
            return pages > 1;
        },
        pageCount(): number {
            return Math.ceil(this.total / this.count);
        },
        hasServerItems(): boolean {
            // Проверяем наличие элементов в серверном списке
            return this.initialTotal > 0;
        },
        shouldShowAlert(): boolean {
            // Показываем alert только если нет элементов ни в серверном, ни в Vue списке
            if (!this.showVueListCW) {
                // Если серверный список еще виден, проверяем серверные данные
                return !this.hasServerItems;
            } else {
                // Если серверный список скрыт, проверяем Vue список
                return this.works.length === 0;
            }
        },
    },
    methods: {
        async onApplyFilter(eventData: { type: string; filters: any[]; page?: number }) {
            if (eventData.type !== "cw") return;

            // Скрываем серверный блок при первом применении фильтра/пагинации
            // Если это пагинация и список уже переключен, не нужно переключать снова
            if (!this.showVueListCW) {
                this.replaceServerListWithVue();
                // Убеждаемся, что Vue обновил DOM перед загрузкой данных
                await this.$nextTick();
            }

            // Убеждаемся, что filters - это массив
            let filtersArray: any[] = [];
            if (Array.isArray(eventData.filters)) {
                filtersArray = eventData.filters;
            } else if (
                eventData.filters &&
                typeof eventData.filters[Symbol.iterator] === "function"
            ) {
                // Если это итерируемый объект
                filtersArray = Array.from(eventData.filters, (f: any) => ({
                    id: f.id,
                    value: f.value,
                }));
            }

            // Если filters пустой, используем текущие фильтры (для пагинации)
            if (filtersArray.length === 0 && eventData.page !== undefined) {
                // Для пагинации используем существующие фильтры
                filtersArray = [...this.lastAppliedFilters];
            }

            // Разделяем на обычные фильтры и сортировку
            const newSort = filtersArray.find((f) => f.id === "sort");
            const newRegularFilters = filtersArray.filter(
                (f) => f.id !== "sort"
            );

            // Объединяем обычные фильтры: новые заменяют старые с тем же id
            const existingRegularFilters = this.lastAppliedFilters.filter(
                (f) => f.id !== "sort"
            );
            const mergedRegularFilters = [...existingRegularFilters];
            for (const newFilter of newRegularFilters) {
                const index = mergedRegularFilters.findIndex(
                    (f) => f.id === newFilter.id
                );
                if (index >= 0) {
                    mergedRegularFilters[index] = newFilter;
                } else {
                    mergedRegularFilters.push(newFilter);
                }
            }

            // Добавляем сортировку (новая или существующая)
            const existingSort = this.lastAppliedFilters.find(
                (f) => f.id === "sort"
            );
            const sort = newSort || existingSort;

            this.lastAppliedFilters = sort
                ? [...mergedRegularFilters, sort]
                : mergedRegularFilters;

            // Если указана страница, используем её, иначе сбрасываем на 1
            const targetPage = eventData.page !== undefined ? eventData.page : 1;
            this.currentPage = targetPage;
            await this.loadPageItems(this.lastAppliedFilters, targetPage);

            // Увеличиваем paginationKey после загрузки данных, чтобы пагинация обновилась с правильными данными
            this.paginationKey += 1;
        },
        async onResetFilter(eventData: { type: string }) {
            if (eventData.type !== "cw") return;

            // Скрываем серверный блок при сбросе фильтра
            this.replaceServerListWithVue();

            this.lastAppliedFilters = [];
            this.paginationKey += 1;
            this.currentPage = 1;
            await this.loadPageItems([], 1);
        },
        async loadPageItems(filters: any[], page: number = 1) {
            this.loading = true;
            this.scrollTo();
            this.currentPage = page;
            try {
                const filterParams = this.buildFilterParams(filters);
                const result = await ConstructionWorkApi.getFilteredWorks(
                    filterParams,
                    page
                );

                this.works = result?.list || [];
                if (result?.total !== undefined) {
                    this.total = result.total;
                }

                // Эмитим новый total обратно в родителя
                this.$emit('update:total', this.total);

                // Убеждаемся, что Vue обновил DOM перед обновлением пагинации
                await this.$nextTick();
            } catch (error) {
                console.error("Error loading:", error);
                this.works = [];
                this.total = 0;
                this.$emit('update:total', this.total);
            } finally {
                this.loading = false;
            }
        },
        buildFilterParams(filters: any[]) {
            const params = { ...this.initialFilters };

            if (this.isArchiveMode) {
                params.type = ["archive"];
            }

            if (!filters || filters.length === 0) {
                return params;
            }

            for (const filter of filters) {
                const value = filter.value.toServerParam();
                if (value !== null && value !== undefined) {
                    // Для сортировки передаем массив строк в формате ["field=order"]
                    if (filter.id === "sort") {
                        params.sorting = value;
                    } else {
                        params[filter.id] = Array.isArray(value)
                            ? value
                            : [value];
                    }
                }
            }

            return params;
        },
        async onPageChange(page: number) {
            // Если список еще не переключен на Vue, значит мы работаем с серверным списком
            if (!this.showVueListCW) {
                // Сразу переключаемся на Vue список, чтобы показать скелетон
                this.replaceServerListWithVue();
            }
            await this.loadPageItems(this.lastAppliedFilters, page);
        },
        replaceServerListWithVue() {
            if (this.showVueListCW) return;

            this.showVueListCW = true;
            const blockCW = document.getElementById("listConstructionWork");
            if (blockCW) {
                blockCW.classList.add("d-none");
            }
        },
        getState() {
            return {
                works: [...this.works],
                total: this.total,
                currentPage: this.currentPage,
                lastAppliedFilters: [...this.lastAppliedFilters],
                showVueListCW: this.showVueListCW,
                paginationKey: this.paginationKey,
            };
        },
        setState(state: any) {
            if (state) {
                this.works = state.works || [...this.initialWorks];
                this.total = state.total !== undefined ? state.total : this.initialTotal;
                this.currentPage = state.currentPage || 1;
                this.lastAppliedFilters = state.lastAppliedFilters || [];
                this.showVueListCW = state.showVueListCW || false;
                this.paginationKey = state.paginationKey || 1;

                // Управляем видимостью серверного блока
                const blockCW = document.getElementById("listConstructionWork");
                if (blockCW) {
                    if (this.showVueListCW) {
                        blockCW.classList.add("d-none");
                    } else {
                        blockCW.classList.remove("d-none");
                    }
                }
            }
        },
        scrollTo(){
            const target = document.querySelector('.m-pageCW-nav');
            const headerOffset = 100;
            const scrollTop = (window.scrollY !== undefined) ? window.scrollY : (window.pageYOffset !== undefined ? window.pageYOffset : document.documentElement.scrollTop);

            const elementPosition = target.getBoundingClientRect().top;
            const offsetPosition = elementPosition + scrollTop - headerOffset;

            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
            });
        },
    },
    mounted() {
        // Инициализируем с начальными данными
        this.works = [...this.initialWorks];
        this.total = this.initialTotal;
        // Серверный блок остается видимым до первого действия пользователя
    },
    emits: ['update:total'],
});
